import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  MapPin, Users, CalendarDays, Building2, Wrench, ScrollText,
  Flag, LayoutDashboard, LogOut, Menu, X,
} from 'lucide-react';
import { useAuth } from '@/stores/auth';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: typeof ScrollText;
  disabled?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/venues', label: 'Площадки', icon: MapPin },
  { to: '/users', label: 'Пользователи', icon: Users },
  { to: '/activities', label: 'Игры', icon: CalendarDays },
  { to: '/orgs', label: 'Клубы', icon: Building2 },
  { to: '/tools', label: 'Инструменты', icon: Wrench },
  { to: '/reports', label: 'Жалобы', icon: Flag },
  { to: '/audit', label: 'Аудит', icon: ScrollText },
];

function initialOf(email: string | null | undefined): string {
  if (!email) return '?';
  const ch = email.trim()[0];
  return ch ? ch.toUpperCase() : '?';
}

function Brand({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  return (
    <div className={cn(
      'flex items-center gap-2 font-medium tracking-tight',
      size === 'md' ? 'text-[14px]' : 'text-[13px]',
    )}>
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#ff5c35] shadow-[0_0_8px_rgba(255,92,53,0.6)]" />
      <span className="text-zinc-100">Huddle</span>
      <span className="text-zinc-500">admin</span>
    </div>
  );
}

function NavList({
  path,
  onItemClick,
  itemPadY,
}: {
  path: string;
  onItemClick?: () => void;
  /** Tailwind class controlling vertical padding (lets us go bigger on mobile drawer) */
  itemPadY?: string;
}) {
  return (
    <nav className="flex-1 px-2 space-y-0.5">
      {NAV.map((item) => {
        const active = path === item.to || (item.to !== '/' && path.startsWith(item.to));
        const Icon = item.icon;
        const content = (
          <span
            className={cn(
              'relative flex items-center gap-2.5 pl-3 pr-3 rounded-md text-[13px] transition-colors',
              itemPadY ?? 'py-1.5',
              active
                ? 'text-zinc-50 bg-zinc-900/40'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/40',
              item.disabled && 'opacity-40 cursor-not-allowed',
            )}
          >
            {/* Active left rail */}
            <span
              className={cn(
                'absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-sm transition-opacity',
                active ? 'bg-[#ff5c35] opacity-100' : 'opacity-0',
              )}
            />
            <Icon className={cn('h-3.5 w-3.5', active ? 'text-zinc-200' : 'text-zinc-500')} />
            <span>{item.label}</span>
          </span>
        );
        return item.disabled
          ? <div key={item.to}>{content}</div>
          : <Link key={item.to} to={item.to} onClick={onItemClick}>{content}</Link>;
      })}
    </nav>
  );
}

function UserFooter({ me, logout }: { me: { email: string | null } | null; logout: () => void }) {
  return (
    <div className="mx-3 mb-3 mt-4 rounded-md border border-zinc-900 bg-zinc-950/60 p-3">
      <div className="flex items-center gap-2.5">
        <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 border border-zinc-800 flex items-center justify-center text-[11px] font-medium text-zinc-200">
          {initialOf(me?.email)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-zinc-300 truncate" title={me?.email ?? ''}>
            {me?.email ?? '—'}
          </div>
          <button
            onClick={logout}
            className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <LogOut className="h-2.5 w-2.5" /> выйти
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile top bar — sticky, sits above main content on < lg.
 * Hidden at ≥ lg where the desktop sidebar takes over.
 */
function MobileTopbar({ onMenuOpen }: { onMenuOpen: () => void }) {
  return (
    <header className="lg:hidden sticky top-0 z-30 h-14 border-b border-zinc-900/80 bg-bg-1/85 backdrop-blur supports-[backdrop-filter]:bg-bg-1/70">
      {/* Decorative top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5c35]/40 to-transparent" />
      <div className="h-full flex items-center justify-between px-3">
        <button
          type="button"
          onClick={onMenuOpen}
          aria-label="Открыть меню"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-300 hover:bg-zinc-900/60 hover:text-zinc-100 transition-colors"
        >
          <Menu className="h-4 w-4" />
        </button>
        <Brand size="md" />
        {/* Right-side spacer to balance the hamburger so brand stays centered */}
        <div className="h-10 w-10" aria-hidden />
      </div>
    </header>
  );
}

/**
 * Slide-from-left drawer. We use the Radix Dialog primitives directly so we can
 * override the default centered Dialog content geometry without forking the
 * shared <DialogContent> primitive used elsewhere in the app.
 */
function MobileDrawer({
  open,
  onOpenChange,
  path,
  me,
  logout,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  path: string;
  me: { email: string | null } | null;
  logout: () => void;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          aria-label="Навигация"
          className="lg:hidden fixed left-0 top-0 z-50 flex h-full w-[280px] max-w-[85vw] flex-col border-r border-zinc-800/80 bg-bg-1 shadow-[8px_0_24px_rgba(0,0,0,0.6)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left duration-200"
        >
          <DialogPrimitive.Title className="sr-only">Навигация</DialogPrimitive.Title>
          {/* Decorative top gradient line */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-[#ff5c35]/40 to-transparent" />

          {/* Brand + close */}
          <div className="px-5 pt-5 pb-6 flex items-center justify-between">
            <Brand />
            <DialogPrimitive.Close
              aria-label="Закрыть меню"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-900/60 hover:text-zinc-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Section label */}
          <div className="px-5 pb-2 text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-medium">
            Навигация
          </div>

          <NavList
            path={path}
            onItemClick={() => onOpenChange(false)}
            itemPadY="py-3"
          />

          <UserFooter me={me} logout={logout} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const me = useAuth((s) => s.me);
  const logout = useAuth((s) => s.logout);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen lg:flex bg-bg-0">
      {/* Desktop sidebar — hidden below lg */}
      <aside className="hidden lg:flex w-56 shrink-0 border-r border-zinc-800/80 bg-bg-1 flex-col">
        {/* Decorative top gradient line */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-[#ff5c35]/40 to-transparent" />

        {/* Brand */}
        <div className="px-5 pt-5 pb-6">
          <Brand />
        </div>

        {/* Section label */}
        <div className="px-5 pb-2 text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-medium">
          Навигация
        </div>

        <NavList path={path} />

        <UserFooter me={me} logout={logout} />
      </aside>

      {/* Mobile top bar + drawer */}
      <MobileTopbar onMenuOpen={() => setDrawerOpen(true)} />
      <MobileDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        path={path}
        me={me}
        logout={logout}
      />

      <main className="flex-1 lg:overflow-y-auto bg-bg-0">{children}</main>
    </div>
  );
}
